import { defineStore } from 'pinia'
import { store } from '../index'
import {
  ProfileType,
  CosSupervisorProfileType,
  RoleFunctionType,
  UserLoginType,
  UserType
} from '@/api/User/types'
import { ElMessageBox } from 'element-plus'
import { useI18n } from '@/hooks/web/useI18n'
import { useTagsViewStore } from './tagsView'
import router from '@/router'
import { systemEnums } from '@/enumerations'

interface UserState {
  userInfo?: UserType
  tokenKey: string
  token: string
  rememberMe: boolean
  loginInfo?: UserLoginType
  roleInfo?: RoleType
  traineeInfo?: ProfileType
  fellowInfo?: CosSupervisorProfileType
  roleRouters?: string[] | RoleFunctionType[] | AppCustomRouteRecordRaw[]
}

export interface RoleType {
  isTrainee?: boolean
  isCosSupervisor?: boolean
  isCollegeAdmin?: boolean
  isSystemAdmin?: boolean
  isBasicTrainee?: boolean
  isHigherTrainee?: boolean
  isCOS?: boolean
  isSupervisor?: boolean
  isViewTrainee?: boolean
}

export const useUserStore = defineStore('user', {
  state: (): UserState => {
    return {
      userInfo: undefined,
      tokenKey: 'Authorization',
      token: '',
      // 记住我
      rememberMe: false,
      loginInfo: undefined,
      traineeInfo: undefined,
      fellowInfo: undefined,
      roleRouters: undefined,
      roleInfo: {
        isTrainee: false,
        isCosSupervisor: false,
        isCollegeAdmin: false,
        isSystemAdmin: false,
        isBasicTrainee: false,
        isHigherTrainee: false,
        isSupervisor: false,
        isCOS: false,
        isViewTrainee: false
      }
    }
  },
  getters: {
    getUserInfo(): UserType | undefined {
      return this.userInfo
    },
    getLoginInfo(): UserLoginType | undefined {
      return this.loginInfo
    },
    getRoleInfo(): RoleType | undefined {
      return this.roleInfo
    },
    getTraineeInfo(): ProfileType | undefined {
      return this.traineeInfo
    },
    getFellowInfo(): CosSupervisorProfileType | undefined {
      return this.fellowInfo
    },
    getRoleRouters(): string[] | RoleFunctionType[] | AppCustomRouteRecordRaw[] | undefined {
      return this.roleRouters
    },
    getTokenKey(): string {
      return this.tokenKey
    },
    getToken(): string {
      return this.token
    },
    getRememberMe(): boolean {
      return this.rememberMe
    }
  },
  actions: {
    setUserInfo(userInfo?: UserType) {
      this.setRoleInfo(userInfo)
      // this.setRoleRouters(userInfo?.roleFunctions || [])

      const { isTrainee, isSupervisor, isCOS } = this.roleInfo as RoleType
      if (isTrainee) {
        this.setTraineeInfo(userInfo?.traineeProfile)
      }
      if (isSupervisor || isCOS) {
        this.setFellowInfo(userInfo?.cosSupervisorProfile)
      }

      this.userInfo = userInfo
    },
    setTraineeInfo(traineeInfo: ProfileType | undefined) {
      this.traineeInfo = traineeInfo
    },
    setFellowInfo(fellowInfo: CosSupervisorProfileType | undefined) {
      this.fellowInfo = fellowInfo
    },
    setLoginInfo(loginInfo: UserLoginType | undefined) {
      this.loginInfo = loginInfo
    },
    setRoleInfo(userInfo?: UserType) {
      const roleInfo = {
        isTrainee: false,
        isCosSupervisor: false,
        isCollegeAdmin: false,
        isSystemAdmin: false,
        isBasicTrainee: false,
        isHigherTrainee: false,
        isSupervisor: false,
        isCOS: false,
        isViewTrainee: false
      }

      if (userInfo) {
        const { user } = userInfo

        roleInfo.isTrainee = user.roles.includes(systemEnums.BasicRole.Trainee)
        roleInfo.isCosSupervisor = user.roles.includes(systemEnums.BasicRole.CosSupervisor)
        roleInfo.isCollegeAdmin = user.roles.includes(systemEnums.BasicRole.CollegeAdmin)
        roleInfo.isSystemAdmin = user.roles.includes(systemEnums.BasicRole.SystemAdmin)
        roleInfo.isBasicTrainee = user.roles.includes(systemEnums.SystemRole.BasicTrainee_BST)
        roleInfo.isHigherTrainee = user.roles.includes(systemEnums.SystemRole.HigherTrainee_HOT)
        roleInfo.isSupervisor = user.roles.includes(systemEnums.SystemRole.Supervisor)
        roleInfo.isCOS = user.roles.includes(systemEnums.SystemRole.COS)
      }

      this.roleInfo = roleInfo
    },
    setViewTraineeRole(isViewTrainee: boolean) {
      if (this.roleInfo) {
        this.roleInfo.isViewTrainee = isViewTrainee
      }
    },
    setRoleRouters(roleRouters: string[] | RoleFunctionType[] | AppCustomRouteRecordRaw[]) {
      this.roleRouters = roleRouters
    },
    setTokenKey(tokenKey: string) {
      this.tokenKey = tokenKey
    },
    setToken(token: string) {
      this.token = token
    },
    setRememberMe(rememberMe: boolean) {
      this.rememberMe = rememberMe
    },
    logoutConfirm() {
      const { t } = useI18n()
      ElMessageBox.confirm(t('common.loginOutMessage'), t('common.reminder'), {
        confirmButtonText: t('common.ok'),
        cancelButtonText: t('common.cancel'),
        type: 'warning'
      })
        .then(async () => {
          // const res = await loginOutApi().catch(() => {})
          // if (res) {
          this.reset()
          // }
        })
        .catch(() => {})
    },
    reset() {
      const { isSystemAdmin } = this.getRoleInfo as RoleType
      const tagsViewStore = useTagsViewStore()
      tagsViewStore.delAllViews()
      this.setToken('')
      this.setRoleInfo(undefined)
      this.setUserInfo(undefined)
      this.setFellowInfo(undefined)
      this.setTraineeInfo(undefined)
      this.setRoleRouters([])

      if (isSystemAdmin) {
        router.replace('/admin-login')
      } else {
        router.replace('/login')
      }
    },
    logout() {
      this.reset()
    }
  },
  persist: {
    key: 'user',
    storage: sessionStorage
  }
})

export const useUserStoreWithOut = () => {
  return useUserStore(store)
}
